import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { entitiesApi, filesApi } from "@/lib/api";
import CurrencyInput from "@/components/CurrencyInput";
import CurrencySelect from "@/components/CurrencySelect";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon, Type, LayoutList } from "lucide-react";

const SUMMARY_OPTIONS = [
  { value: "current_priority", label: "Current priority" },
  { value: "next_action", label: "Next action" },
  { value: "upcoming_event", label: "Upcoming event" },
  { value: "phase", label: "Phase" },
  { value: "objective", label: "Objective" },
  { value: "status", label: "Status" },
];

const empty = {
  name: "", description: "", color: "#3B82F6", background_image: "",
  status: "Active", phase: "", objective: "", current_priority: "",
  next_action: "", upcoming_event: "", approved_budget: 0,
  summary_line_1: "current_priority", summary_line_2: "upcoming_event",
  display_mode: "detail", logo_file_id: null,
};

const MODES = [
  { value: "detail", label: "Detail", icon: LayoutList, hint: "Name plus two summary lines" },
  { value: "text",   label: "Text",   icon: Type,       hint: "Only the entity name, large and centered" },
  { value: "logo",   label: "Logo",   icon: ImageIcon,  hint: "Only the uploaded logo — no text on the card" },
];

export default function EntityDialog({ open, setOpen, entity, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    if (entity) setForm({ ...empty, ...entity, display_mode: entity.display_mode || "detail" });
    else setForm(empty);
  }, [entity, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["png", "jpg", "jpeg", "svg", "webp"].includes(ext)) {
      return toast.error("Logo must be PNG, JPG, SVG, or WebP");
    }
    setUploadingLogo(true);
    try {
      const r = await filesApi.upload(file);
      set("logo_file_id", r.data.id);
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const clearLogo = () => set("logo_file_id", null);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.display_mode === "logo" && !form.logo_file_id) {
      return toast.error("Please upload a logo before saving in Logo mode");
    }
    setSaving(true);
    try {
      if (entity?.id) await entitiesApi.update(entity.id, form);
      else await entitiesApi.create(form);
      toast.success(entity?.id ? "Entity updated" : "Entity created");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const logoUrl = form.logo_file_id ? filesApi.downloadUrl(form.logo_file_id) : null;
  const mode = form.display_mode || "detail";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#151A23] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="entity-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{entity?.id ? "Edit entity" : "New entity"}</DialogTitle>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <div className="col-span-2">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Name</Label>
            <Input data-testid="entity-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
          </div>

          {/* Display mode selector */}
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Card display mode</Label>
            <div className="mt-2 grid grid-cols-3 gap-2" role="radiogroup" data-testid="display-mode-group">
              {MODES.map((m) => {
                const Icon = m.icon;
                const selected = mode === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => set("display_mode", m.value)}
                    data-testid={`display-mode-${m.value}`}
                    className={`flex flex-col items-start gap-1.5 p-3 rounded-lg border text-left transition-colors ${
                      selected
                        ? "border-white/40 bg-white/10"
                        : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${selected ? "text-white" : "text-white/60"}`} />
                    <div className={`text-sm font-medium ${selected ? "text-white" : "text-white/80"}`}>{m.label}</div>
                    <div className="text-[11px] text-white/50 leading-snug">{m.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Logo upload — always visible so users can pre-upload, but REQUIRED in logo mode */}
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider flex items-center gap-2">
              Logo {mode === "logo" && <span className="text-red-400 normal-case tracking-normal text-[11px]">Required for Logo mode</span>}
            </Label>
            <div className="mt-2">
              {logoUrl ? (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.03]">
                  <div className="w-16 h-16 rounded-md bg-[#0B0E14] flex items-center justify-center overflow-hidden flex-shrink-0">
                    <img src={logoUrl} alt="Logo preview" className="max-w-full max-h-full object-contain" data-testid="entity-logo-preview" />
                  </div>
                  <div className="flex-1 min-w-0 text-xs text-white/60">Logo uploaded. It will preserve its aspect ratio and fit cleanly on the card.</div>
                  <button
                    type="button"
                    onClick={clearLogo}
                    className="p-1.5 rounded hover:bg-white/10 text-white/60"
                    aria-label="Remove logo"
                    data-testid="entity-logo-remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg,.webp,image/png,image/jpeg,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])}
                    data-testid="entity-logo-input"
                  />
                  <div className={`flex items-center justify-center gap-2 p-6 rounded-lg border border-dashed text-sm text-white/70 hover:bg-white/[0.04] transition-colors ${
                    mode === "logo" ? "border-red-400/40 bg-red-400/[0.03]" : "border-white/15 bg-white/[0.02]"
                  }`}>
                    <Upload className="w-4 h-4" />
                    {uploadingLogo ? "Uploading…" : "Upload PNG, JPG, SVG, or WebP"}
                  </div>
                </label>
              )}
            </div>
          </div>

          <div className="col-span-2">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Description</Label>
            <Textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" rows={2} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">Color</Label>
              <div className="flex gap-2 mt-1.5">
                <Input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} className="w-16 h-10 p-1 bg-white/5 border-white/10" data-testid="entity-color-input" />
                <Input value={form.color} onChange={(e) => set("color", e.target.value)} className="flex-1 bg-white/5 border-white/10" />
              </div>
            </div>
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">Status</Label>
              <Input value={form.status || ""} onChange={(e) => set("status", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">Objective</Label>
              <Input value={form.objective || ""} onChange={(e) => set("objective", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" data-testid="entity-objective-input"/>
            </div>
            <div className="col-span-2">
              <Label className="text-white/60 text-xs uppercase tracking-wider">Background image URL (optional)</Label>
              <Input value={form.background_image || ""} onChange={(e) => set("background_image", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" placeholder="https://…" />
            </div>
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">Approved budget</Label>
              <CurrencyInput value={form.approved_budget} currency={form.currency || "USD"} onChange={(v) => set("approved_budget", parseFloat(v) || 0)} className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">Currency</Label>
              <div className="mt-1.5"><CurrencySelect value={form.currency || "USD"} onChange={(v) => set("currency", v)}/></div>
            </div>
            <div />
            {mode === "detail" && (
              <>
                <div>
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Card line 1</Label>
                  <Select value={form.summary_line_1} onValueChange={(v) => set("summary_line_1", v)}>
                    <SelectTrigger className="mt-1.5 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#151A23] border-white/10 text-white">
                      {SUMMARY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/60 text-xs uppercase tracking-wider">Card line 2</Label>
                  <Select value={form.summary_line_2} onValueChange={(v) => set("summary_line_2", v)}>
                    <SelectTrigger className="mt-1.5 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#151A23] border-white/10 text-white">
                      {SUMMARY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="entity-save-btn" className="rounded-full bg-white text-black hover:bg-white/90">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
